import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { getExternalAuth } from "lib/firebase";
import { adminRequest } from "lib/adminSiteApi";
import { ErrorNotice } from "components/admin/workspace/Controls";
import "./AdminWorkspace.css";
export default function AdminInvitationPage() {
  const [proof] = useState(() => {
    const p = new URLSearchParams(window.location.hash.slice(1));
    return { invitationId: p.get("invitation"), token: p.get("token") };
  });
  const [user, setUser] = useState(null),
    [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(null),
    nav = useNavigate();
  useEffect(() => {
    window.history.replaceState(null, "", window.location.pathname);
    const old = document.title;
    document.title = "Accept Admin invitation | Magellan";
    const auth = getExternalAuth();
    if (!auth) {
      setError(new Error("Website identity is not configured."));
      return () => {
        document.title = old;
      };
    }
    const stop = onAuthStateChanged(auth, setUser);
    return () => {
      stop();
      document.title = old;
    };
  }, []);
  const run = async (fn) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const login = (create = false) =>
    run(async () => {
      const auth = getExternalAuth();
      if (!auth) throw new Error("Website identity is not configured.");
      await (
        create ? createUserWithEmailAndPassword : signInWithEmailAndPassword
      )(auth, email, password);
      setPassword("");
    });
  return (
    <div className="admin-workspace" style={{ display: "block" }}>
      <main className="aw-main" style={{ maxWidth: 650, paddingTop: 60 }}>
        <p className="aw-eyebrow">Magellan · Staff invitation</p>
        <h1>Join the admin workspace</h1>
        <p>
          Sign in with the email address that received this invitation, then
          accept it. Opening the email alone does not grant access.
        </p>
        <ErrorNotice error={error} />
        {!proof.invitationId || !proof.token ? (
          <p role="alert" className="aw-notice">
            Open your invitation from its email. If it has expired, ask an owner
            for a new invitation.
          </p>
        ) : user ? (
          <section className="aw-panel">
            <h2>Confirm your invitation</h2>
            <p>Signed in as {user.email}</p>
            <button
              className="aw-button aw-primary"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await adminRequest("/admin-invitations/accept", {
                    method: "POST",
                    body: proof,
                    user,
                  });
                  nav("/admin", { replace: true });
                })
              }
            >
              {busy ? "Checking invitation…" : "Accept invitation"}
            </button>
            <button
              className="aw-button"
              disabled={busy}
              onClick={() => run(() => signOut(getExternalAuth()))}
            >
              Use another account
            </button>
          </section>
        ) : (
          <section className="aw-panel">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                login();
              }}
            >
              <label>
                Email
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <button className="aw-button aw-primary" disabled={busy}>
                Sign in
              </button>
              <button
                type="button"
                className="aw-button"
                disabled={busy || !email || password.length < 12}
                onClick={() => login(true)}
              >
                Create identity account
              </button>
              <p className="aw-muted">
                New account passwords must contain at least 12 characters.
                Creating an identity does not grant staff permissions until you
                accept the invitation.
              </p>
            </form>
            <button
              className="aw-button"
              disabled={busy}
              onClick={() =>
                run(() =>
                  signInWithPopup(getExternalAuth(), new GoogleAuthProvider()),
                )
              }
            >
              Sign in with Google
            </button>
          </section>
        )}
        <Link className="aw-link" to="/admin/login">
          Back to admin login
        </Link>
      </main>
    </div>
  );
}
